/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

/* Libraries */
import { css } from '@emotion/react';
import {
    AppBar,
    Toolbar,
    IconButton,
    Drawer,
    List,
    Button,
    Menu,
    MenuItem,
    Tooltip,
    TextField,
    FormControl,
    InputAdornment,
    DialogTitle,
    Dialog,
    DialogContent,
    Typography, Grid2 as Grid
} from "@mui/material";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Avatar from 'react-avatar';
import dayjs from "dayjs";

/* Components */
import DatePickerComp from './DatePickerComp';
import DatePickerRangeComp from './DatePickerRangeComp';

/* Services */
import {
    fetchOrganizationUsers,
    getStatus,
    fetchOrganizationDocuments,
    fetchPortfolios,
    userLogout
} from "../../services/GraphService";

/* Actions */
import {
    changeRenderType,
    changeChosenPortfolioData,
    toggleSignalTableDialog,
    changeStatusInfo,
    changeOrganizationUsers,
    changeOrganizationDocuments,
    changeDateRange,
    changeUserInfo,
    changeDate,
    changePortfoliosData
} from '../../store/actions';

/* Icons */
import {
    Menu as MenuIcon,
    Close as CloseIcon,
    Logout as LogoutIcon,
} from "@mui/icons-material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadIcon from '@mui/icons-material/Upload';
import TableRowsIcon from '@mui/icons-material/TableRows';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import SettingsIcon from '@mui/icons-material/Settings';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ContactsIcon from '@mui/icons-material/Contacts';

/* Styles */
import * as styles from "./NavbarStyles";

/* Logo */
import logo from './logo.svg';
import {fetchStatus} from "../../App";

/* Store */
const mapState = (reducer) => ({
    renderType: reducer?.renderType,
    user: reducer?.user,
    portfolios: reducer?.portfolios,
    chosenPortfolio: reducer?.chosenPortfolio,
    statusInfo: reducer?.statusInfo,
    disableUploadButton: reducer?.disableUploadButton,
    startDate: reducer?.dateRange?.startDate,
    endDate: reducer?.dateRange?.endDate,
    showPasswordExpireSoonError:reducer?.passwordExpirationError,
    date: reducer?.date
});

const Navbar = ({ showChangePasswordForm, daysLeftForPasswordExpiration }) => {
    const dispatch = useDispatch();

    const { renderType, user, portfolios, chosenPortfolio, disableUploadButton, startDate, endDate, date } = useSelector(mapState);

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const [anchorElProfile, setAnchorElProfile] = useState(null);
    const openProfile = Boolean(anchorElProfile);

    const [anchorElChangeProfile, setAnchorElChangeProfile] = useState(null);
    const openChangeProfile = Boolean(anchorElChangeProfile);

    const [anchorElDatepicker, setAnchorElDatepicker] = useState(false);
    const openDatepicker = Boolean(anchorElDatepicker);

    const [tradingType, setTradingType] = useState(null);

    const today = dayjs()?.startOf("day");

    const userFeatures = user?.organizations?.find((organization) => organization?.organization_id === user?.organization_id) || [];

    /* Render first active trading */
    const renderFirstActiveTradingTypeByPriority = () => {
        const checkIfIntradayTradingExist = userFeatures?.features?.find((feature) => feature === 'INTRADAY_TRADING');
        const checkIfUserReportsExist = userFeatures?.features?.find((feature) => feature === 'USER_REPORTS');
        const checkIfDayAheadTradingExist = userFeatures?.features?.find((feature) => feature === 'DAY_AHEAD_TRADING');

        if (checkIfIntradayTradingExist) {
            return setTradingType('intraday');
        }

        if (checkIfUserReportsExist) {
            return setTradingType('userReports');
        }

        if (checkIfDayAheadTradingExist) {
            return setTradingType('dayAhead');
        }
    }

    /* Check if feature exist by given @label and @array (conditionally) */
    const checkIfFeatureExist = (featureLabel, givenFeaturesArray = null) => {
        const properFeatureArray = givenFeaturesArray ? givenFeaturesArray : userFeatures?.features;
        const findFeature = properFeatureArray?.find((feature) => feature === featureLabel);

        let showFeature = false;

        if (findFeature) {
            showFeature = true;
        }

        return showFeature;
    }

    useEffect(() => {
        renderFirstActiveTradingTypeByPriority();
    }, [user?.organizations]);

    /* Toggle drawer based on click */
    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    /* Handle click anchor */
    const handleClick = (event, type) => {
        const target = event?.currentTarget;

        switch (type) {
            case 'profile':
                setAnchorElProfile(target);
                break;

            case 'changeProfile':
                setAnchorElChangeProfile(target);
                break;

            case 'datepicker':
                setAnchorElDatepicker(target);
                break;

            default:
                break;
        }
    };

    /* Handle close anchor */
    const handleClose = () => {
        if (anchorElProfile) { setAnchorElProfile(null); }
        if (anchorElChangeProfile) { setAnchorElChangeProfile(null); }
        if (anchorElDatepicker) { setAnchorElDatepicker(null); }
    };

    /* Render element based on @type */
    const handleRenderType = (type) => {
        if (type) {
            handleClose();

            if (type !== renderType) {
                dispatch(changeRenderType(type));
                setTradingType(type);
            }

            if (type === 'tradeHistory') {
                dispatch(changeDateRange({
                    startDate: null,
                    endDate: null,
                    showDateRange: false
                }))
            }
        }
    }

    /* Handle logout */
    const handleLogout = () => {
        setAnchorElProfile(null);

        userLogout().then(r => {
            sessionStorage.removeItem('token');
            window.location.reload();
        });
    }

    /* Proper portfolio label */
    const portfolioLabel = (portfolioInfo) => {
        if (!portfolioInfo || !Object?.keys(portfolioInfo)?.length) { return; }

        let interval = '';

        switch (portfolioInfo?.delivery_period) {
            case 60:
                interval = '1 hour';
                break;

            case 15:
                interval = '15 min';
                break;

            default:
                break;
        }

        return `${portfolioInfo?.name} ( ${interval} )`;
    }

    /* Handle change chosen portfolio data */
    const handleChangePortfolio = (portfolio) => {
        if (JSON?.stringify(portfolio) === JSON?.stringify(chosenPortfolio)) { return; }

        dispatch(changeChosenPortfolioData(portfolio));

        const portfolioId = portfolio?.portfolio_id;
        const organizationId = portfolio?.organization_id;

        if (organizationId && portfolioId) {
            getStatus(organizationId, portfolioId)
                .then(result => {
                    if (result) {
                        dispatch(changeStatusInfo(result));
                    }
                });

            fetchOrganizationUsers(organizationId)
                .then(result => {
                    const users = result?.users;

                    if (users?.length) {
                        dispatch(changeOrganizationUsers(users));
                    }
                })
                .catch(error => {
                    console.error("Error:", error);
                });

            fetchOrganizationDocuments(organizationId)
                .then(result => {
                    if (result) {
                        dispatch(changeOrganizationDocuments(result));
                    }
                })
                .catch(error => {
                    console.error("Error:", error);
                });
        }

        setAnchorElChangeProfile(null);
    }

    /* Handle save edited rows */
    const handleSaveEditedRows = () => {
        dispatch(toggleSignalTableDialog(true));
    }

    /* Hide navbar default */
    const hideNavbar = () => {
        let hideNav = false;

        if (renderType === 'SignalTable' || renderType === 'PortfolioTable' || renderType === 'DocumentTable' || renderType === 'tradeHistory' || renderType === 'Users' || renderType === 'resetPassword') {
            hideNav = true;
        }

        return hideNav;
    }

    /* Handle show date range */
    const handleShowDateRange = () => {
        dispatch(changeDateRange({
            showDateRange: true
        }));
    }

    /* Handle change trading type */
    const handleChangeTradingType = (type) => {
        if (type) {
            setTradingType(type);

            dispatch(changeRenderType(type));
        }
    }

    /*Handle render to proper type */
    const handleRenderProperView = (features) => {
        if (features?.length) {
            let type = 'DocumentTable';

            if(checkIfFeatureExist('INTRADAY_TRADING', features)) {
                type = 'intraday';
            } else if (checkIfFeatureExist('DAY_AHEAD_TRADING', features)) {
                type = 'dayAhead';
            } else if (checkIfFeatureExist('USER_REPORTS', features)) {
                type = 'userReports'
            }

            dispatch(changeRenderType(type));
            return handleChangeTradingType(type);
        }

        return null;
    }
    /* Handle change organization */
    const handleChangeOrganization = (e) => {
        const organization = user?.organizations.find((org) => org?.organization_id === e?.target?.value);

        if(organization) {
            const orgId = organization?.organization_id;

            if (orgId) {
                fetchPortfolios(orgId)
                    .then(result => {
                        const portfoliosResult = result?.portfolios;
                        let firstPortfolio = {};
                        let portfolioId = firstPortfolio?.portfolio_id;

                        if (portfoliosResult?.length) {
                            firstPortfolio = portfoliosResult[0];
                            portfolioId = firstPortfolio?.portfolio_id;

                            fetchStatus(orgId, portfolioId)
                                .then(res => {
                                    if (res) {
                                        dispatch(changeStatusInfo(res));
                                    }
                                })
                                .catch(error => {
                                    console.error("Error:", error);
                                });
                        }

                        dispatch(changePortfoliosData(portfoliosResult));
                        dispatch(changeChosenPortfolioData(firstPortfolio));

                        fetchOrganizationUsers(orgId)
                            .then(res => {
                                const users = res?.users;

                                if (users?.length) {
                                    dispatch(changeOrganizationUsers(users));
                                }
                            })
                            .catch(error => {
                                console.error("Error:", error);
                            });

                        fetchOrganizationDocuments(orgId)
                            .then(result => {
                                if (result) {
                                    dispatch(changeOrganizationDocuments(result));
                                }
                            })
                            .catch(error => {
                                console.error("Error:", error);
                            });

                        handleRenderProperView(organization?.features);
                    })
                    .catch(error => {
                        console.error("Error:", error);
                    });

                dispatch(changeUserInfo({
                    organization_id: orgId,
                    organization: organization?.name
                }));
            }
        }
    }

    /* Change date */
    const handleChangeDate = (date) => {
        if (date) {
            dispatch(changeDate(date));
        }
    }

    /* Clear date */
    const handleClearDate = () => dispatch(changeDate(null));

    /* Hande change start date */
    const handleStartDateChange = (date) => {
        if (date) {
            dispatch(changeDateRange({
                startDate: dayjs(date),
                endDate: null,
                showDateRange: false
            }));
        }
    };

    /* Hande change end date */
    const handleEndDateChange = (date) => {
        if (date) {
            dispatch(changeDateRange({
                endDate: dayjs(date),
                showDateRange: false
            }));
        }
    };

    /* Hande clear start date */
    const handleClearStartDate = () => {
        dispatch(changeDateRange({
            startDate: null,
            endDate: null,
            showDateRange: false
        }));
    };

    /* Hande clear end date */
    const handleClearEndDate = () => {
        dispatch(changeDateRange({
            endDate: null,
            showDateRange: false
        }));
    };

    return (
        <React.Fragment>
            {/* AppBar with logo and hamburger icon */}
            <AppBar position="fixed" sx={styles.appBar}>
                <Toolbar sx={styles.toolbar}>
                    <img src={logo} alt="" width={250} />     {/**LOGO */}
                    {hideNavbar()
                        ? <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                                variant="contained"
                                css={styles.backBtn}
                                onClick={() => handleRenderProperView(userFeatures?.features)}
                                startIcon={<ArrowBackIcon />}
                            >
                                Назад
                            </Button>

                            {renderType !== 'resetPassword' && (
                                <div css={styles.portfolioName}>
                                    {chosenPortfolio?.name && (<>Портфолио: <span> {chosenPortfolio?.name} </span></>)}
                                </div>
                            )}

                            {renderType === 'SignalTable' &&
                                <Button
                                    variant="contained"
                                    css={styles.uploadBtn}
                                    onClick={handleSaveEditedRows}
                                    startIcon={<UploadIcon />}
                                    disabled={disableUploadButton}
                                >
                                    Прикачи
                                </Button>
                            }

                            {renderType === 'tradeHistory' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 40 }}>
                                    <DatePickerRangeComp
                                        startDate={startDate}
                                        endDate={endDate}
                                        onStartDateChange={handleStartDateChange}
                                        onEndDateChange={handleEndDateChange}
                                        onStartDateClear={handleClearStartDate}
                                        onEndDateClear={handleClearEndDate}
                                    />

                                    <Button
                                        variant="contained"
                                        css={styles.showBtn}
                                        onClick={handleShowDateRange}
                                        disabled={!startDate || startDate === today}
                                    >
                                        Покажи
                                    </Button>
                                </div>
                            )}
                        </div>
                        : <>
                            {/*<div css={[styles.separator]} />*/}

                            {/*<Tooltip*/}
                            {/*    placement="bottom"*/}
                            {/*    title="Начална страница"*/}
                            {/*    slotProps={{*/}
                            {/*        tooltip: {*/}
                            {/*            style: {*/}
                            {/*                backgroundColor: '#636468',*/}
                            {/*                fontFamily: 'Manrope',*/}
                            {/*                fontSize: 12,*/}
                            {/*                letterSpacing: '0.5px',*/}
                            {/*                padding: '5px 10px'*/}
                            {/*            }*/}
                            {/*        },*/}
                            {/*        popper: {*/}
                            {/*            modifiers: [*/}
                            {/*                {*/}
                            {/*                    name: 'offset',*/}
                            {/*                    options: {*/}
                            {/*                        offset: [0, -3] // set distance from the icon for the tooltip*/}
                            {/*                    }*/}
                            {/*                }*/}
                            {/*            ]*/}
                            {/*        }*/}
                            {/*    }}*/}
                            {/*>*/}
                            {/*    <HomeIcon css={[styles.homeIcon, renderType === 'graph' ? styles.activeHomeIcon : '']} onClick={() => handleRenderType('graph')} />*/}
                            {/*</Tooltip>*/}

                            <div css={[styles.separator]} />

                            {/* Hamburger Icon */}
                            <IconButton
                                edge="end"
                                color="inherit"
                                aria-label="menu"
                                onClick={toggleDrawer}
                                sx={{ display: { xs: 'block', sm: 'none', marginTop: '.5rem' } }} /* Show only on small screens */
                            >
                                {isDrawerOpen ? <CloseIcon /> : <MenuIcon />} {/* Change icon based on state */}
                            </IconButton>

                            <div css={styles.navLinks}>
                                {checkIfFeatureExist("DAY_AHEAD_TRADING") && (
                                    <>
                                        <h3 css={styles.navLink} className={tradingType === 'dayAhead' ? 'active' : ''} onClick={() => handleChangeTradingType('dayAhead')}>Търговия „Ден напред“</h3>
                                        <div css={styles.separator} />
                                    </>
                                )}

                                {checkIfFeatureExist("INTRADAY_TRADING") && (
                                    <>
                                        <h3 css={styles.navLink} className={tradingType === 'intraday' ? 'active' : ''} onClick={(event) => handleChangeTradingType('intraday')}>Търговия „Текущ ден“</h3>
                                        <div css={styles.separator} />
                                    </>
                                )}

                                {checkIfFeatureExist("USER_REPORTS") && (
                                    <>
                                        <h3 css={styles.navLink} className={tradingType === 'userReports' ? 'active' : ''} onClick={() => handleChangeTradingType('userReports')}>Справки</h3>
                                        <div css={styles.separator} />
                                    </>
                                )}

                                <h3 css={styles.navLink} onClick={() => handleRenderType('DocumentTable')}>Документи</h3>

                                {/*<Tooltip*/}
                                {/*    placement="bottom"*/}
                                {/*    title='Контакти'*/}
                                {/*    slotProps={{*/}
                                {/*        tooltip: {*/}
                                {/*            style: {*/}
                                {/*                backgroundColor: '#636468',*/}
                                {/*                fontFamily: 'Manrope',*/}
                                {/*                fontSize: 12,*/}
                                {/*                letterSpacing: '0.5px',*/}
                                {/*                padding: '5px 10px'*/}
                                {/*            }*/}
                                {/*        },*/}
                                {/*        popper: {*/}
                                {/*            modifiers: [*/}
                                {/*                {*/}
                                {/*                    name: 'offset',*/}
                                {/*                    options: {*/}
                                {/*                        offset: [0, 4] // set distance from the icon for the tooltip*/}
                                {/*                    }*/}
                                {/*                }*/}
                                {/*            ]*/}
                                {/*        }*/}
                                {/*    }}*/}
                                {/*>*/}
                                {/*    <ContactsIcon*/}
                                {/*        css={css`*/}
                                {/*            ${styles.contactIcon};*/}
                                {/*            font-size: 2rem !important;*/}
                                {/*            position:relative;*/}
                                {/*            top:-3px;*/}
                                {/*        `}*/}
                                {/*        size={'small'}*/}
                                {/*        onClick={() => handleRenderType('Users')} />*/}
                                {/*</Tooltip>*/}

                                <div css={styles.profileHolder}>
                                    <div>
                                        <h3>
                                            Клиент:
                                            <span>
                                                {
                                                    user?.super_user
                                                        ?   user?.organizations?.length
                                                                ?   <div css={styles.selectWrapper}>
                                                                        <select css={styles.organizationSelect} onChange={handleChangeOrganization}>
                                                                            {user?.organizations.map((organization, key) => {
                                                                                if (organization?.active) {
                                                                                    return <option key={key} value={organization?.organization_id} selected={organization?.organization_id === user?.organization_id}>{organization?.name}</option>
                                                                                }
                                                                            })}
                                                                        </select>

                                                                        <div css={styles.arrow}>
                                                                            <svg
                                                                                width="20"
                                                                                height="20"
                                                                                viewBox="0 0 20 20"
                                                                                fill="currentColor"
                                                                            >
                                                                              <path d="M5 7l5 5 5-5H5z" />
                                                                            </svg>
                                                                        </div>
                                                                    </div>
                                                                :   user?.organization
                                                        :   user?.organization
                                                }
                                            </span>
                                        </h3>
                                    </div>
                                    {
                                        showChangePasswordForm && (
                                            <Tooltip
                                                placement="bottom"
                                                title={`Your password expire in ${daysLeftForPasswordExpiration} days. You can change it now!`}
                                                slotProps={{
                                                    tooltip: {
                                                        style: {

                                                            backgroundColor: '#636468',
                                                            fontFamily: 'Manrope',
                                                            fontSize: 12,
                                                            letterSpacing: '0.5px',
                                                            padding: '5px 10px'
                                                        }
                                                    },
                                                    popper: {
                                                        modifiers: [
                                                            {
                                                                name: 'offset',
                                                                options: {
                                                                    offset: [0, 3] // set distance from the icon for the tooltip
                                                                }
                                                            }
                                                        ]
                                                    }
                                                }}
                                            >
                                                <LockOpenIcon css={styles.changePasswordIcon} onClick={() => handleRenderType('resetPassword')} />
                                            </Tooltip>
                                        )
                                    }

                                    <Tooltip
                                        placement="bottom"
                                        title={`${user?.name} (${user?.organization})`}
                                        slotProps={{
                                            tooltip: {
                                                style: {

                                                    backgroundColor: '#636468',
                                                    fontFamily: 'Manrope',
                                                    fontSize: 12,
                                                    letterSpacing: '0.5px',
                                                    padding: '5px 10px'
                                                }
                                            },
                                            popper: {
                                                modifiers: [
                                                    {
                                                        name: 'offset',
                                                        options: {
                                                            offset: [0, 3] // set distance from the icon for the tooltip
                                                        }
                                                    }
                                                ]
                                            }
                                        }}
                                    >
                                        <div>
                                            <Avatar
                                                name={user?.name}
                                                title=""
                                                size={30}
                                                color={openProfile ? "#ff5100" : "#636468"}
                                                aria-controls={openProfile ? 'basic-menu' : undefined}
                                                aria-haspopup="true"
                                                aria-expanded={openProfile ? 'true' : undefined}
                                                onClick={(event) => handleClick(event, 'profile')}
                                                css={styles.avatarIcon}
                                            />
                                        </div>
                                    </Tooltip>
                                </div>

                            </div>
                        </>
                    }
                </Toolbar>

                {
                    renderType === 'intraday' && chosenPortfolio?.portfolio_id && (
                        <div css={styles.intradayHolder}>
                            <div css={styles.navLinks}>
                                <FormControl
                                    style={{
                                        minWidth: '280px'
                                    }}
                                    onClick={(event) => handleClick(event, 'changeProfile')}
                                >
                                    <TextField
                                        name="portfolio"
                                        fullWidth
                                        variant="standard"
                                        value={portfolioLabel(chosenPortfolio)}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton>
                                                        <ArrowDropDownIcon sx={{ fontSize: '30px' }} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                            readOnly: true
                                        }}
                                    />
                                </FormControl>

                                <DatePickerComp
                                    value={date}
                                    onChange={handleChangeDate}
                                    onClear={handleClearDate}
                                />

                                <div css={[styles.separator]} />

                                <h4 css={styles.navLink} onClick={() => handleRenderType('SignalTable')}> Сигнали </h4>

                                <div css={styles.separator} />

                                <h4 css={styles.navLink} onClick={() => handleRenderType('tradeHistory')}> Резултати </h4>

                                <div css={[styles.separator]} />

                                <h4 css={styles.navLink} onClick={() => handleRenderType('PortfolioTable')}> Настройки </h4>
                            </div>
                        </div>
                    )
                }
            </AppBar>

            {/* Drawer for mobile */}
            <Drawer
                anchor="left"
                open={isDrawerOpen}
                onClose={toggleDrawer}
                sx={styles.drawer}
            >
                <div css={styles.drawerContent}>
                    <List>

                    </List>
                </div>
            </Drawer>

            <Menu
                id="basic-menu-profile"
                anchorEl={anchorElProfile}
                open={openProfile}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
                sx={{
                    top: '6px',
                    '& .MuiList-root': {
                        background: '#636468'
                    }
                }}
            >
                <MenuItem css={styles.menuItem} style={{ cursor: 'default' }}> {user?.email} </MenuItem>
                <MenuItem css={styles.menuItem} onClick={handleLogout}>
                    <LogoutIcon style={{ marginRight: 5 }} />
                    Изход
                </MenuItem>
            </Menu>

            <Menu
                id="basic-menu-change-profile"
                anchorEl={anchorElChangeProfile}
                open={openChangeProfile}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
                sx={{
                    top: '7px',
                    left: '0px',

                    '& .MuiList-root': {
                        background: '#636468'
                    }
                }}
            >
                {
                    portfolios?.map((portfolio, key) => {
                        return <MenuItem key={key} css={styles.menuItem} onClick={() => handleChangePortfolio(portfolio)}> {portfolioLabel(portfolio)} </MenuItem>
                    })
                }
            </Menu>

            <Menu
                id="basic-menu-datepicker"
                anchorEl={anchorElDatepicker}
                open={openDatepicker}
                position={'left'}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
                sx={{
                    top: '5px',
                    left: '-140px',

                    '& .MuiList-root': {
                        padding: '0px!important'
                    }
                }}
            >
                <DatePickerComp
                    value={date}
                    onChange={handleChangeDate}
                    onClear={handleClearDate}
                />
            </Menu>
        </React.Fragment>
    );
};

export default Navbar;